import { detectEmbeddingUrlLocale, isSparkleDomain } from "#/shared/embedding/publishing-domain";

export const DEFAULT_LOCALE = "en";

// Keep in sync with `config.i18n.available_locales` (config/application.rb). An
// unrecognized `?locale=` is ignored rather than trusted: otherwise the frontend
// would initialize vue-i18n with a bogus locale and request a nonexistent
// catalog while the server has already fallen back to English.
const AVAILABLE_LOCALES = new Set(["en", "es", "pt", "ht", "zh-CN", "ar", "fr"]);

// A valid `?locale=` is the primary signal when explicitly passed (it survives
// even when Sparkle content is embedded in a host page whose own <html lang> we
// don't control), followed by the page's locale path prefix, the locale the
// server published, <html lang> (set when Sparkle renders the page standalone)
// and finally English. On a host CMS page that takes the locale in its path
// (`cmsHostTakesPathLocale`) the path prefix alone decides, and `?locale=` and
// <html lang> are ignored.
function localeFromUrl() {
  try {
    const param = new URLSearchParams(window.location.search).get("locale") || "";
    return AVAILABLE_LOCALES.has(param) ? param : "";
  } catch {
    return "";
  }
}

// The locale the page is addressed under: the first path segment when it names
// a non-default locale the client exposes, `/es/physicians/jane-doe` → "es".
// The server serves a prefix only for `SparkleConfig.locales.available`;
// without that config, any translated locale counts. It is read from the URL
// rather than from `window.Sparkle.locale` because it is known before any
// deferred page script runs. A host CMS page's path names a locale only when
// that host takes the locale in its path (`hostTakesPathLocale`): a host that
// takes it as a param may have a `/pt/physical-therapy` page of its own.
function localeFromPath() {
  if (!hostTakesPathLocale()) {
    return "";
  }

  const locale = normalizeLocale(window.location.pathname.split("/")[1]);
  const clientLocales = window.SparkleConfig?.locales?.available;

  if (locale === DEFAULT_LOCALE || (clientLocales && !clientLocales.includes(locale))) {
    return "";
  }

  return locale;
}

// Whether the page's path can carry the locale: a Sparkle page always can, and a
// host CMS page can when it takes the locale in its path (`cmsHostTakesPathLocale`).
// Without `SparkleConfig.embedPublishingDomains` the page is taken for a Sparkle page.
function hostTakesPathLocale() {
  return !window.SparkleConfig?.embedPublishingDomains || isSparkleDomain() || cmsHostTakesPathLocale();
}

// Whether the page is on a host CMS whose embedding endpoint is configured with
// `url_locale: path` in publishing_domains.yml. There the path alone addresses the language:
// `/es/find-a-doctor` is Spanish and a bare path the default locale, whatever
// `?locale=` or the host's `<html lang>` say, as the embed bundle's
// `Sparkle.Embed.Utilities.currentLocale` reads it. The endpoint is matched the
// way `detectEmbeddingUrlLocale` matches it, so the two resolvers agree.
function cmsHostTakesPathLocale() {
  return (
    Boolean(window.SparkleConfig?.embedPublishingDomains) && !isSparkleDomain() && detectEmbeddingUrlLocale() === "path"
  );
}

// The server publishes its resolved locale to `window.Sparkle.locale` (see the
// `sparkle_locale_js` Rails helper). When Sparkle is embedded in a host page we
// don't control its `<html lang>` (often a hardcoded "en-US"), so this
// Sparkle-set signal is preferred over `<html lang>` — but still below an
// explicit `?locale=`. Validated like `?locale=` so a bogus value is ignored.
function localeFromSparkle() {
  try {
    const locale = (window.Sparkle && window.Sparkle.locale) || "";
    return AVAILABLE_LOCALES.has(locale) ? locale : "";
  } catch {
    return "";
  }
}

// `<html lang>` is often a regional tag (e.g. "en-US" from an embedding CMS's
// template), so normalize it to an available locale — exact match, else the
// primary language subtag — rather than trusting it verbatim. Otherwise we'd
// initialize vue-i18n under (and request a catalog for) a locale the server
// would ignore.
function localeFromDocument() {
  return normalizeCandidate(document.documentElement.lang);
}

// Exact (case-insensitive) match against the available locales, else the
// candidate's primary language subtag, else "".
function normalizeCandidate(candidate) {
  return normalizeLocale(candidate) || normalizeLocale((candidate || "").split("-")[0]);
}

function normalizeLocale(candidate) {
  if (!candidate) {
    return "";
  }

  const candidateLower = candidate.toLowerCase();

  for (const locale of AVAILABLE_LOCALES) {
    if (locale.toLowerCase() === candidateLower) {
      return locale;
    }
  }

  return "";
}

export function resolveLocale(locale) {
  // The explicit argument is normalized like every other signal — an
  // unrecognized value falls through to the next source instead of
  // initializing vue-i18n under a locale the server would ignore.
  const explicit = normalizeCandidate(locale);

  if (explicit) {
    return explicit;
  }

  if (cmsHostTakesPathLocale()) {
    return localeFromPath() || DEFAULT_LOCALE;
  }

  return localeFromUrl() || localeFromPath() || localeFromSparkle() || localeFromDocument() || DEFAULT_LOCALE;
}

// The locale to forward on Sparkle-bound requests: the resolved page locale, or
// null for the default so default-language URLs stay param-free and cacheable.
export function requestLocale() {
  const locale = resolveLocale();

  return locale === DEFAULT_LOCALE ? null : locale;
}

function embedCurrentLocale() {
  return window.Sparkle?.Embed?.Utilities?.currentLocale?.();
}

function pageQueryLocale(pageUrl) {
  try {
    return new URL(pageUrl()).searchParams.get("locale");
  } catch {
    return null;
  }
}

/**
 * Returns the locale an embedded Sparkle request carries: the embed's `currentLocale()`, else the page's
 * `?locale=`. Returns null when neither is set, or when the locale is the default one or not available.
 *
 * @param {object} [options]
 * @param {string[]} [options.available] - `SparkleConfig.locales.available` when omitted
 * @param {string} [options.defaultLocale] - `SparkleConfig.locales.default`, else `DEFAULT_LOCALE`, when omitted
 * @param {() => string|null|undefined} [options.currentLocale] - the embed's locale
 * @param {() => string} [options.pageUrl] - the page address
 */
export function embedRequestLocale({
  available = window.SparkleConfig?.locales?.available ?? [],
  defaultLocale = window.SparkleConfig?.locales?.default ?? DEFAULT_LOCALE,
  currentLocale = embedCurrentLocale,
  pageUrl = () => window.location.href,
} = {}) {
  const locale = currentLocale() || pageQueryLocale(pageUrl);

  return locale && available.includes(locale) && locale !== defaultLocale ? locale : null;
}
